<template>
  <template v-if="modelValue.Id === 'kpi_savings_rate'">
    <TargetRangeDonutChart
      valueSuffix="potential savings rate"
      :modelValue="modelValue.Value"
      :targetRange="[targetRangeStart, targetRangeEnd]"
    />
  </template>

  <template v-else-if="modelValue.Id === 'kpi_credit_card_debt_to_income'">
    <TargetRangeDonutChart
      valueSuffix="credit card debt to income ratio"
      :modelValue="modelValue.Value"
      :targetRange="[targetRangeStart, targetRangeEnd]"
    />
  </template>

  <template v-else-if="modelValue.Id === 'kpi_monthly_debt_to_income_ratio'">
    <TargetRangeDonutChart
      valueSuffix="monthly debt-to-income ratio"
      :modelValue="modelValue.Value"
      :targetRange="[targetRangeStart, targetRangeEnd]"
    />
  </template>

  <template v-else-if="modelValue.Id === 'kpi_retirement_progress'">
    <TargetRangeDonutChart
      v-if="!isOverRetirementTarget"
      valueSuffix="of retirement savings goal"
      :modelValue="modelValue.Value / 100"
      :targetRange="[(modelValue.Benchmark?.TargetMin ?? 0) / 100, (modelValue.Benchmark?.TargetMax ?? 100) / 100]"
    />

    <AssetLiabilitiesBarChart
      v-else-if="isOverRetirementTarget"
      valueLabelPrefix="Retirement savings"
      v-bind="{scale}"
      scaleType="asymmetric"
      :assetAmount="retirementBalance"
      :valueLabel="formatAmount(modelValue.SourceValues['retirementBalance'].Amount, {excludeCents: true}) || undefined"
      :targetRange="[retirementTargetRangeStart, (modelValue.Benchmark?.TargetMax ?? 100) / 100]"
      :targetRangeLabel="`Target: ${formatAmount(retirementTarget, {excludeCents: true}) || undefined}`"
      :targetRangeIntervalValue="retirementTarget"
    />
  </template>

  <template v-else-if="modelValue.Id === 'kpi_net_worth'">
    <AssetLiabilitiesBarChart
      valueLabelPrefix="Net worth"
      :assetAmount="modelValue.SourceValues['assets'].Amount"
      :liabilitiesAmount="modelValue.SourceValues['liabilities'].Amount"
      :valueLabel="formatAmount(modelValue.Value, {excludeCents: true}) || undefined"
      v-bind="{scale}"
      includeLegend
    />
  </template>

  <template v-else-if="modelValue.Id === 'kpi_emergency_fund'">
    <AssetLiabilitiesBarChart
      valueLabelPrefix="Cash"
      targetRangeLabel="Target range in months"
      :assetAmount="modelValue.SourceValues['cash'].Amount"
      :targetRange="[targetRangeStart, targetRangeEnd]"
      :targetRangeIntervalValue="modelValue.SourceValues['monthlyNecessities'].Amount"
      :valueLabel="formatAmount(modelValue.SourceValues['cash'].Amount, {excludeCents: true}) || undefined"
      v-bind="{scale}"
    />
  </template>
</template>

<script setup lang="ts">
import AssetLiabilitiesBarChart from "@cosine/components/AssetLiabilitiesBarChart.vue";
import TargetRangeDonutChart from "@cosine/components/TargetRangeDonutChart.vue";
import { IFinancialInsightsMetric } from "@cosine/lib/api-models";
import formatAmount from "@cosine/lib/utils/financial/formatAmount";
import formatPercent from "@cosine/lib/utils/financial/formatPercent";
import formatNumberToCompact from "@cosine/lib/utils/number/formatNumberToCompact";
import { computed } from "vue";

const modelValue = defineModel<IFinancialInsightsMetric>({
  required: true,
});

const targetRangeStart = computed(() => modelValue.value.Benchmark?.TargetMin ?? 0);
const targetRangeEnd = computed(() => modelValue.value.Benchmark?.TargetMax ?? 1);

const scale = computed(() => getScaleForMetric());

const retirementBalance = computed(() => modelValue.value.SourceValues["retirementBalance"].Amount);
const retirementTarget = computed(() => modelValue.value.SourceValues["targetRetirement"].Amount);
const isOverRetirementTarget = computed(() => retirementBalance.value > retirementTarget.value);
const retirementMaxValue = computed(() => Math.max(retirementBalance.value, retirementTarget.value));
const retirementTargetRangeStart = computed(() => (!isOverRetirementTarget.value ? targetRangeStart.value : (retirementTarget.value / retirementMaxValue.value)));

function getScaleForMetric () {
  switch (modelValue.value.Id) {
    case "kpi_emergency_fund":
      return getEmergencyFundScale();
    case "kpi_net_worth":
      return getNetWorthScale();
    case "kpi_retirement_progress":
      return getRetirementProgressScale();
    default:
      return [];
  }
}

function getRetirementProgressScale () {
  const zeroPoint = {
    label: formatPercent(0) || undefined,
    value: 0,
  };

  const retirementSavingsPoint = {
    label: formatPercent(retirementBalance.value / retirementTarget.value, {
      decimalPlaceCount: 0,
    }) || undefined,
    value: retirementBalance.value,
  };

  const retirementTargetPoint = {
    label: formatPercent(1, {
      decimalPlaceCount: 0,
    }) || undefined,
    value: retirementTarget.value,
  };

  const points = [
    zeroPoint,
    retirementSavingsPoint,
    retirementTargetPoint,
  ].sort((a, b) => a.value - b.value).reverse();

  const minValue = 0;
  const maxValue = Math.max(retirementBalance.value, retirementTarget.value);
  const valueRange = maxValue - minValue;

  return points.map((point) => ({
    ...point,
    y: (point.value - minValue) / valueRange,
  }));
}

function getEmergencyFundScale () {
  const metric = modelValue.value;
  const targetRangeEnd = metric.Benchmark?.TargetMax ?? 0;
  const maxValueInMonths = Math.max(targetRangeEnd, metric.Value);

  let intervalsInMonths;

  if (maxValueInMonths > 50) {
    intervalsInMonths = [
      ...new Set(Array.from(
        {
          length: Math.ceil(Math.log10(maxValueInMonths) * 10),
        },
        (_, i) => Math.round(10 ** (i / 10)),
      )),
    ];
  } else {
    intervalsInMonths = Array.from({
      length: maxValueInMonths,
    }, (_, i) => i + 1);
  }

  return [0, ...intervalsInMonths].map((point, index, array) => {
    const isFirstOrLast = [0, array.length - 1].includes(index);
    const isSecondOrSecondLast = [1, array.length - 2].includes(index);

    return {
      label: (point % 3 === 0 && !isSecondOrSecondLast) || isFirstOrLast ? point.toString() : undefined,
      value: point,
    };
  }).reverse();
}

function getNetWorthScale () {
  const metric = modelValue.value;
  const liabilities = metric.SourceValues["liabilities"].Amount;
  const netWorth = metric.Value;
  const cash = metric.SourceValues["assets"].Amount;

  const liabilityPoint = {
    label: "$" + formatNumberToCompact(liabilities / 100),
    value: -liabilities,
  };

  const zeroPoint = {
    label: formatAmount(0, {
      excludeCents: true,
    }) || undefined,
    value: 0,
  };

  const netWorthPoint = {
    label: "$" + formatNumberToCompact(netWorth / 100),
    value: netWorth,
    isHighlighted: true,
  };

  const cashPoint = {
    label: "$" + formatNumberToCompact(cash / 100),
    value: cash,
  };

  const points = [
    liabilityPoint,
    ...(netWorth !== 0 ? [zeroPoint] : []),
    netWorthPoint,
    cashPoint,
  ].sort((a, b) => a.value - b.value).reverse();

  const minValue = -liabilities;
  const maxValue = Math.max(cash, netWorth);
  const valueRange = maxValue - minValue;

  return points.map((point) => ({
    ...point,
    y: (point.value - minValue) / valueRange,
  }));
}
</script>
